import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useController, useWatch } from 'react-hook-form';
import { useLocation, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FaWhatsapp } from 'react-icons/fa6';
import { LuBanknote, LuCalendarDays, LuClock, LuHash, LuLaptopMinimal } from 'react-icons/lu';
import { useIdempotencyKey } from '@mds/shared/hooks/use-idempotency-key';
import { ApiError } from '@mds/shared/lib/api';
import {
  FREE_TRIAL_BOOKING_END_DATE,
  FREE_TRIAL_BOOKING_START_DATE,
  FREE_TRIAL_BOOKING_WINDOW_LABEL,
  FREE_TRIAL_EARLIEST_END_TIME,
  FREE_TRIAL_END_TIME,
  FREE_TRIAL_LATEST_START_TIME,
  FREE_TRIAL_START_TIME,
  FREE_TRIAL_TIME_WINDOW_LABEL,
  isFreeTrialBookingDate,
} from '@mds/shared/lib/free-trial';
import { cn } from '@mds/shared/lib/utils';
import { bookingEndDate } from '@mds/shared/lib/booking-dates';
import { minutesOf, todayInTimeZone } from '@mds/shared/lib/dates';
import { clockRange } from '@mds/shared/lib/format';
import { SeatMap, SpacePicker, SPACES, type SpaceId } from '@mds/shared/seat-map';
import type { GuestBookingQuote, GuestBookingRequestReceipt } from '@mds/shared/types/guest-booking';
import { Alert } from '@mds/shared/ui/feedback/alert';
import { ChoiceList } from '@mds/shared/ui/form/choice-list';
import { Field } from '@mds/shared/ui/form/field';
import { DatePicker } from '@mds/shared/ui/form/date-picker';
import { Form, applyServerErrors, useZodForm } from '@mds/shared/ui/form/form';
import { FormSection } from '@mds/shared/ui/form/form-section';
import { PhoneInput } from '@mds/shared/ui/form/phone-input';
import { Button } from '@mds/shared/ui/shadcn/button';
import { Input } from '@mds/shared/ui/shadcn/input';
import Select from '@mds/shared/ui/select';

import PageHeader from '@/components/heading/page-header';
import PolicyConsent from '@/components/form/policy-consent';
import OutcomePanel from '@/components/section/outcome-panel';
import WhenPicker from '@/components/form/when-picker';
import { ErrorBlock } from '@/components/states';
import { BOOKING_POLICY, CONTACT } from '@/data';

import {
  OCCUPATION_LABELS,
  PAYMENT_METHOD_LABELS,
  REGION_LABELS,
  WORK_MODE_LABELS,
  deskBookingSchema,
  isTrialTime,
  type DeskPaymentMethod,
  type DeskBooking,
  type Region,
  type WorkMode,
} from '@/features/booking/desk-booking.schema';
import { packageQueries } from '@/features/packages/packages.queries';
import { getQuote, requestBooking } from '@/features/spaces/spaces.api';
import { spaceKeys, spaceQueries } from '@/features/spaces/spaces.queries';
import { formatLongDate, formatMoney, formatPackageUnit } from '@/lib/format';

/** A label map as the options of a select or a radio list, in its own order. */
const optionsOf = (labels: Record<string, string>) =>
  Object.entries(labels).map(([value, label]) => ({ value, label }));

/** How a desk can be paid for. */
const PAYMENT_OPTIONS = optionsOf(PAYMENT_METHOD_LABELS);
const REGION_OPTIONS = optionsOf(REGION_LABELS);
const OCCUPATION_OPTIONS = optionsOf(OCCUPATION_LABELS);
const WORK_MODE_OPTIONS = optionsOf(WORK_MODE_LABELS);
const FACILITY_TIMEZONE = 'Asia/Beirut';
const FREE_TRIAL_PACKAGE_ITEM_ID = 'free-trial';

/**
 * A saved request, as the confirmation shows it and the history entry keeps it.
 * `whatsAppOpened` is whether the browser let the chat open by itself, which
 * decides whether the confirmation asks for the send or only offers it again.
 */
type SentBooking = {
  receipt: GuestBookingRequestReceipt;
  values: DeskBooking;
  whatsAppOpened: boolean;
};

/**
 * A part of the form that glides open and shut rather than snapping. The rows
 * of a one-row grid go from `0fr` to `1fr`, which a browser can transition
 * where it cannot transition `height: auto`; only the space moves, the fields
 * inside are not animated.
 *
 * Shut, it stays mounted (so the glide has something to close over) but is
 * `inert`: nothing in it can be focused or read out. `-mx-1 px-1` and `pb-1`
 * give the fields' focus rings room the clipping would cut off; no negative
 * margin below, which would pull the form up while shut.
 */
function Expand({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div
      inert={!open}
      className={cn(
        'grid transition-[grid-template-rows] duration-(--duration-base) ease-standard motion-reduce:transition-none',
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      )}
    >
      <div className="-mx-1 min-h-0 overflow-hidden">
        <div className="flex flex-col gap-3 px-1 pt-3 pb-1">{children}</div>
      </div>
    </div>
  );
}

/**
 * A control in the map card's toolbar, labelled like the floor picker beside
 * it: a small mono label above, the hint or the schema's message below. The
 * error replaces the hint rather than joining it, as in `Field`. The `<label>`
 * wraps the control, so it names it without an id.
 */
function ToolbarField({
  label,
  hint,
  error,
  className,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label className="flex flex-col gap-1.5">
        <span className="font-mono text-2xs text-ink-muted uppercase">{label}</span>
        {children}
      </label>
      {/* `tracking-normal`, as on `Field`'s message: the `xs` size carries the
          wide tracking of the mono labels, which a sentence does not want. */}
      {error ? (
        <p className="text-xs tracking-normal text-danger-text">{error}</p>
      ) : (
        hint && <p className="text-xs tracking-normal text-ink-muted">{hint}</p>
      )}
    </div>
  );
}

/**
 * The page, remounted when the offer changes (the footer's plain link from the
 * trial page, say), so the form starts over with the other mode's schema.
 */
export default function BookPage() {
  const [params] = useSearchParams();
  const trial = params.get('offer') === 'trial';
  return <DeskBookingPage key={trial ? 'trial' : 'paid'} trial={trial} />;
}

/**
 * Booking a daily desk: pick a desk on the floor map, give a name and email,
 * choose a package and how to pay, accept the booking policy, then save the
 * request and send it on WhatsApp with the booking already written out. Staff
 * confirm the stored request from the admin app.
 *
 * One form, checked by `deskBookingSchema`. The map, the package rows and the
 * policy box are not inputs `Field` can bind, so they are bound with
 * `useController` and show the schema's message themselves.
 *
 * The floor is in the URL (`?floor=upper`). A desk space's slug is its floor's
 * map key, so the floor's own page links here with its slug and the map and
 * the floor dropdown open on that floor. Being in the URL rather than router
 * state, it survives a refresh, a new tab and a shared link; picking the other
 * floor in the dropdown rewrites it. With no floor given (the home hero, the
 * footer), the first floor opens.
 *
 * Back steps back one page (`navigate(-1)`) when the visitor came from inside
 * the site, wherever that was. Landed here from outside, it goes instead to the
 * path the sending page passed as router state (`<Link state={{ from }}>`), or
 * the floor's own page, or the spaces list when there is no floor either.
 *
 * `?offer=trial` (the home page's free trial week) books the system free-trial package on either
 * floor: no package choice or payment, plus the visitor's time window, where they live, and whether
 * they study (and what) or work (how, and in what field).
 */
function DeskBookingPage({ trial }: { trial: boolean }) {
  const { state } = useLocation();
  const [params, setParams] = useSearchParams();

  const asked = params.get('floor');
  const floor: SpaceId = SPACES.find((s) => s.id === asked)?.id ?? SPACES[0].id;
  const from: string = state?.from ?? (asked === floor ? `/spaces/${floor}` : '/spaces');
  const today = todayInTimeZone(FACILITY_TIMEZONE);
  const initialBookingDate = trial
    ? today < FREE_TRIAL_BOOKING_START_DATE
      ? FREE_TRIAL_BOOKING_START_DATE
      : today <= FREE_TRIAL_BOOKING_END_DATE
        ? today
        : ''
    : today;

  // Desk packages are the same on both floors, so the list is not per space.
  // The free-trial package is system-owned and hidden from the regular paid package list.
  const packages = useQuery({ ...packageQueries.list(), enabled: !trial });
  const spaces = useQuery(spaceQueries.list());
  const queryClient = useQueryClient();

  const schema = useMemo(() => deskBookingSchema(trial, today), [trial, today]);
  const form = useZodForm(schema, {
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      bookingDate: initialBookingDate,
      startTime: '',
      endTime: '',
      desk: '',
      packageId: '',
      paymentMethod: '',
      region: '',
      occupation: '',
      major: '',
      workMode: '',
      workField: '',
      policyAccepted: false,
    },
  });
  // The questions after "student or employee" depend on the answer.
  const occupation = useWatch({ control: form.control, name: 'occupation' });
  // Each message shows after the first send and clears as soon as its gap is
  // filled: the form re-checks on every change once it has been submitted.
  const desk = useController({ name: 'desk', control: form.control });
  // The map, so a send with no desk scrolls back up to it (see the map below).
  const mapRef = useRef<HTMLDivElement>(null);
  const pkg = useController({ name: 'packageId', control: form.control });
  const bookingDate = useController({ name: 'bookingDate', control: form.control });
  const startTime = useController({ name: 'startTime', control: form.control });
  const endTime = useController({ name: 'endTime', control: form.control });
  const policy = useController({ name: 'policyAccepted', control: form.control });
  const selectedPackage = packages.data?.find((item) => item.id === pkg.field.value);
  // By id rather than `selectedPackage`: after a reload the form is empty, the saved request not.
  const packageName = (id: string) => packages.data?.find((item) => item.id === id)?.name ?? id;
  const resourceId = spaces.data?.find((space) => space.slug === floor)?.id ?? '';
  const trialTimeReady =
    !trial ||
    (isTrialTime(startTime.field.value) &&
      isTrialTime(endTime.field.value) &&
      startTime.field.value >= FREE_TRIAL_START_TIME &&
      endTime.field.value <= FREE_TRIAL_END_TIME &&
      endTime.field.value > startTime.field.value);
  const trialStartMinute = trial && trialTimeReady ? minutesOf(startTime.field.value) : undefined;
  const trialEndMinute = trial && trialTimeReady ? minutesOf(endTime.field.value) : undefined;
  const availabilityReady =
    Boolean(resourceId) &&
    bookingDate.field.value >= today &&
    (!trial || isFreeTrialBookingDate(bookingDate.field.value)) &&
    trialTimeReady &&
    (trial || Boolean(selectedPackage));
  const availability = useQuery({
    ...spaceQueries.deskAvailability(
      resourceId,
      bookingDate.field.value,
      trial ? FREE_TRIAL_PACKAGE_ITEM_ID : selectedPackage?.id,
      trialStartMinute,
      trialEndMinute,
    ),
    enabled: availabilityReady,
  });
  const unavailableDesks = useMemo(
    () => availability.data?.filter((item) => !item.available).map((item) => item.label) ?? [],
    [availability.data],
  );
  const reservationEndDate =
    bookingDate.field.value && selectedPackage
      ? bookingEndDate(bookingDate.field.value, selectedPackage.duration)
      : bookingDate.field.value;
  const idempotency = useIdempotencyKey(`idem:desk-booking-request:${resourceId || floor}`);
  /** A failure no field can show, with the page's own words when it has them. */
  const [formError, setFormError] = useState<{ error: unknown; message?: string } | null>(null);
  /** The new total after `BOOKING_QUOTE_CHANGED`, until the next send. */
  const [newTotal, setNewTotal] = useState<number | null>(null);

  // A refetch can find the picked desk taken: by someone else since it was
  // picked, or under a newly picked package that holds it for longer. Empty it
  // then, so the schema's "Pick a desk" stops the send. A desk still free stays.
  useEffect(() => {
    if (!desk.field.value || !availability.data) return;
    if (!availability.data.some((item) => item.label === desk.field.value && item.available)) {
      form.setValue('desk', '');
    }
  }, [availability.data, desk.field.value, form]);

  function changeFloor(next: SpaceId) {
    // `replace`, so switching floors does not fill the back button with one
    // entry per switch; the router state is carried over to keep the back link.
    setParams(trial ? { floor: next, offer: 'trial' } : { floor: next }, { replace: true, state });
    // Desk ids are per floor, so a desk picked on the other one means nothing here.
    desk.field.onChange('');
  }

  /** The chat that finishes the booking, the reference first so staff find the saved request. */
  function whatsAppUrl(values: DeskBooking, reference: string) {
    const message = [
      'Hello MDS,',
      '',
      trial ? "I'd like to book a free desk for the trial week:" : "I'd like to book this desk:",
      '',
      `Reference: ${reference}`,
      `Name: ${values.fullName}`,
      // Which package, so the desk's length is in the chat: a day, a week, a month.
      ...(trial ? [] : [`Package: ${packageName(values.packageId)}`]),
      `Date: ${formatLongDate(values.bookingDate)}`,
      ...(trial
        ? [`Time: ${clockRange(minutesOf(values.startTime), minutesOf(values.endTime))}`]
        : []),
      `Desk: ${values.desk}`,
      ...(trial || !values.paymentMethod
        ? []
        : [`Payment: ${PAYMENT_METHOD_LABELS[values.paymentMethod]}`]),
    ].join('\n');
    return `${CONTACT.whatsappHref}?text=${encodeURIComponent(message)}`;
  }

  const bookingRequest = useMutation({
    mutationFn: async (values: DeskBooking) => {
      const selection = {
        resourceId,
        bookingDate: values.bookingDate,
        deskLabel: values.desk,
        deskPackageItemId: trial ? FREE_TRIAL_PACKAGE_ITEM_ID : values.packageId,
        ...(trial
          ? {
              startMinute: minutesOf(values.startTime),
              endMinute: minutesOf(values.endTime),
            }
          : {}),
      };
      const quote = await getQuote(selection);
      const trialDetails =
        trial && values.occupation === 'student'
          ? {
              region: values.region as Region,
              occupation: 'student' as const,
              major: values.major,
              workMode: null,
              workField: null,
            }
          : trial && values.occupation === 'employee'
            ? {
                region: values.region as Region,
                occupation: 'employee' as const,
                major: null,
                workMode: values.workMode as WorkMode,
                workField: values.workField,
              }
            : undefined;
      const body = {
        ...selection,
        guest: { name: values.fullName, email: values.email, phone: values.phone },
        paymentMethod: trial ? ('none' as const) : (values.paymentMethod as DeskPaymentMethod),
        policyAccepted: true as const,
        ...(trialDetails ? { trialDetails } : {}),
        expectedQuote: {
          rateCardVersionId: quote.price.rateCardVersionId,
          totalAmountMinor: quote.price.totalAmountMinor,
        },
      };
      const receipt = await requestBooking({ requestId: idempotency.keyFor(body), ...body });
      return { receipt, values };
    },
    onSuccess: ({ receipt, values }) => {
      idempotency.reset();
      queryClient.invalidateQueries({ queryKey: spaceKeys.all });
      // A new tab where the browser allows one, so this tab stays on the
      // confirmation. One that blocks it after the API round trip hands back
      // `null`, and the confirmation then asks for the send with its button.
      // No `noopener` feature, which makes the result `null` either way: the
      // link back to this page is cut by hand instead.
      const tab = window.open(whatsAppUrl(values, receipt.reference), '_blank');
      if (tab) tab.opener = null;
      // Into this page's history entry, so the confirmation outlives the page:
      // Back or a refresh reloads it, where the mutation's result is gone and
      // the browser would refill the form.
      const sent: SentBooking = { receipt, values, whatsAppOpened: Boolean(tab) };
      setParams(params, { replace: true, state: { ...state, sent } });
    },
    onMutate: () => {
      setFormError(null);
      setNewTotal(null);
    },
    onError: (error) => {
      // Field mistakes the form's own checks did not catch land under the field.
      if (applyServerErrors(error, form.setError)) return;

      const code = error instanceof ApiError ? error.code : undefined;
      /** Said where the problem is, rather than in the alert under the button. */
      const refuse = (name: 'desk' | 'packageId' | 'bookingDate' | 'startTime', message: string) =>
        form.setError(name, { type: 'server', message }, { shouldFocus: true });

      if (code === 'BOOKING_CONFLICT') {
        // `setValue`, not the field's onChange: that re-validates and would
        // put "Pick a desk" over the reason.
        form.setValue('desk', '');
        queryClient.invalidateQueries({ queryKey: spaceKeys.all });
        return refuse('desk', 'That desk was just taken. Pick another one.');
      }
      if (code === 'PACKAGE_UNAVAILABLE') {
        if (trial) {
          return setFormError({
            error,
            message: "Free trial desks aren't available right now. Contact MDS and we'll help.",
          });
        }
        form.setValue('packageId', '');
        queryClient.invalidateQueries({ queryKey: packageQueries.list().queryKey });
        return refuse('packageId', 'This package is no longer on sale. Pick another.');
      }
      if (code === 'BOOKING_TIME_PAST') {
        return trial
          ? refuse('startTime', 'That time has passed. Pick a later one.')
          : refuse('bookingDate', 'That date has passed. Pick a later one.');
      }
      if (code === 'BOOKING_QUOTE_CHANGED') {
        // Every send prices it again, so the next press goes at this price:
        // the visitor only has to see it first.
        const quote = (error as ApiError).body?.details as { quote?: GuestBookingQuote } | undefined;
        if (quote?.quote) return setNewTotal(quote.quote.price.totalAmountMinor);
      }
      if (code === 'BOOKING_REQUEST_ID_REUSED') {
        // The key went out earlier with different details. A new one is made
        // on the next press; the visitor only has to press again.
        idempotency.reset();
        return setFormError({
          error,
          message: 'Your details changed since the last try. Press Book on WhatsApp again.',
        });
      }
      setFormError({ error });
    },
  });

  function submit(values: DeskBooking) {
    bookingRequest.mutate(values);
  }

  // Once saved, the request in place of the form: the summary the visitor sent.
  // Kept in the history entry (written on success), so it is there after a
  // reload too: Back from WhatsApp, a refresh.
  const sent = (state as { sent?: SentBooking } | null)?.sent ?? null;

  if (sent) {
    const { receipt, values, whatsAppOpened } = sent;
    const firstName = values.fullName.split(/\s+/)[0];
    return (
      <section className="mx-auto max-w-page px-gutter pt-section">
        {/* In place of the form, as on the event page: a form still sitting
            there invites sending it twice. WhatsApp opens in its own tab, so the
            way back from it lands here. Success once the chat has opened; the
            "one step left" clock while the send is still the visitor's to make. */}
        <OutcomePanel
          tone={whatsAppOpened ? 'success' : 'warning'}
          icon={whatsAppOpened ? undefined : LuClock}
          title={whatsAppOpened ? `Request received, ${firstName}` : `One step left, ${firstName}`}
          description={
            whatsAppOpened
              ? "We've saved it and opened WhatsApp with your booking written out. Send the message there and we'll confirm your desk."
              : "Your request is saved. Send it to us on WhatsApp and we'll confirm your desk there."
          }
          details={{
            title: trial ? 'Free trial desk' : packageName(values.packageId),
            items: [
              { icon: LuHash, marker: 'Reference', description: receipt.reference },
              {
                icon: LuCalendarDays,
                marker: 'Date',
                description: formatLongDate(values.bookingDate),
              },
              ...(trial
                ? [
                    {
                      icon: LuClock,
                      marker: 'Time',
                      description: clockRange(
                        minutesOf(values.startTime),
                        minutesOf(values.endTime),
                      ),
                    },
                  ]
                : []),
              { icon: LuLaptopMinimal, marker: 'Desk', description: values.desk },
              ...(trial || !values.paymentMethod
                ? []
                : [
                    {
                      icon: LuBanknote,
                      marker: 'Payment',
                      description: PAYMENT_METHOD_LABELS[values.paymentMethod],
                    },
                  ]),
            ],
          }}
          actions={
            // The main action only while the chat has not opened; once it has,
            // a quiet way back to it for a visitor who closed it unsent.
            <div className="flex flex-col items-center gap-2">
              <Button asChild size="lg" variant={whatsAppOpened ? 'outline' : 'default'}>
                <a
                  href={whatsAppUrl(values, receipt.reference)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaWhatsapp aria-hidden="true" />
                  {whatsAppOpened ? 'Open WhatsApp again' : 'Send on WhatsApp'}
                </a>
              </Button>
              {whatsAppOpened && (
                <p className="m-0 text-xs tracking-normal text-ink-muted">
                  Only if you closed it before sending.
                </p>
              )}
            </div>
          }
        />
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-page px-gutter pt-section">
      <PageHeader
        back={{ label: 'Back', to: from, history: true }}
        title={trial ? 'Book your free desk' : 'Book your desk'}
        description={
          trial
            ? `Choose one day from ${FREE_TRIAL_BOOKING_WINDOW_LABEL} and a time during ${FREE_TRIAL_TIME_WINDOW_LABEL}, then continue on WhatsApp with your reference.`
            : "Choose a package and start date, then pick a desk that is free for the full reservation and send it to us on WhatsApp. We'll confirm it there."
        }
      />

      {/* Side by side from `lg`, the map's card on the left and the booking's
          cards on the right; stacked below that, where a column beside the map
          would squeeze both. The gap between them is the one between cards. */}
      <div className="mt-block grid gap-card lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        {/* What the map waits on, said once under the title, where it is read
            before the map rather than found after a press on it. */}
        <FormSection
          title="Pick a desk"
          description={
            trial
              ? 'Choose a date and time to see which desks are free.'
              : 'Choose a package to see which desks are free for all of it.'
          }
        >
          {/* Where and when, in one row above the map: the map shows which
              desks are free for these, so it reads after them. Outside the
              `<form>` like the map, but bound to it all the same. One line
              from `sm`, the boxes sharing it equally; on a phone, a line each,
              so no name is cut short. A trial's day and times are gathered in
              the one `WhenPicker`. */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
            <SpacePicker value={floor} onChange={changeFloor} fill className="flex-1" />
            {trial ? (
              <ToolbarField
                label="When"
                hint={`Free trial: ${FREE_TRIAL_BOOKING_WINDOW_LABEL}, ${FREE_TRIAL_TIME_WINDOW_LABEL}.`}
                // One message for the box: the first of its three.
                error={
                  bookingDate.fieldState.error?.message ??
                  startTime.fieldState.error?.message ??
                  endTime.fieldState.error?.message
                }
                className="min-w-0 flex-1"
              >
                <WhenPicker
                  className="text-sm"
                  aria-invalid={
                    Boolean(
                      bookingDate.fieldState.error ??
                        startTime.fieldState.error ??
                        endTime.fieldState.error,
                    ) || undefined
                  }
                  date={bookingDate.field.value}
                  from={startTime.field.value}
                  to={endTime.field.value}
                  dates={{
                    min:
                      today < FREE_TRIAL_BOOKING_START_DATE
                        ? FREE_TRIAL_BOOKING_START_DATE
                        : today,
                    max: FREE_TRIAL_BOOKING_END_DATE,
                  }}
                  fromTimes={{ min: FREE_TRIAL_START_TIME, max: FREE_TRIAL_LATEST_START_TIME }}
                  toTimes={{ min: FREE_TRIAL_EARLIEST_END_TIME, max: FREE_TRIAL_END_TIME }}
                  onDateChange={(next) => {
                    bookingDate.field.onChange(next);
                    desk.field.onChange('');
                  }}
                  onFromChange={(next) => {
                    startTime.field.onChange(next);
                    desk.field.onChange('');
                  }}
                  onToChange={(next) => {
                    endTime.field.onChange(next);
                    desk.field.onChange('');
                  }}
                  onClose={() => {
                    bookingDate.field.onBlur();
                    startTime.field.onBlur();
                    endTime.field.onBlur();
                  }}
                />
              </ToolbarField>
            ) : (
              <>
              {/* The package beside the date, since the map checks a desk for
                  the package's whole length: everything the map waits on sits
                  above it. The same field as the list in the booking card, so
                  picking in either picks in both. */}
              <ToolbarField
                label="Package"
                error={
                  pkg.fieldState.error?.message ??
                  (packages.isError && (
                    <>
                      We couldn't load the packages.{' '}
                      <button
                        type="button"
                        onClick={() => void packages.refetch()}
                        className="font-medium underline underline-offset-2"
                      >
                        Retry
                      </button>
                    </>
                  ))
                }
                className="min-w-0 flex-1"
              >
                <Select
                  variant="field"
                  aria-label="Package"
                  className="w-full min-w-0 text-sm"
                  aria-invalid={Boolean(pkg.fieldState.error) || undefined}
                  disabled={!packages.data}
                  placeholder={packages.isError ? 'Unavailable' : 'Choose a package'}
                  value={pkg.field.value}
                  onChange={pkg.field.onChange}
                  onBlur={pkg.field.onBlur}
                  // Each price on the right of the open list, as the package
                  // cards write it ("$10 / day"), all ending on one edge.
                  options={(packages.data ?? []).map((p) => ({
                    value: p.id,
                    label: p.name,
                    detail: p.quoteOnly
                      ? 'By quote'
                      : `${formatMoney(p.amountMinor)} ${formatPackageUnit({
                          unit: p.unit,
                          unitLabel: p.unitLabel ?? undefined,
                        })}`,
                  }))}
                />
              </ToolbarField>
              <ToolbarField
                label={selectedPackage?.duration?.unit === 'day' ? 'Date' : 'Start date'}
                // How long the package holds the desk, when that is past the day.
                hint={
                  reservationEndDate && reservationEndDate !== bookingDate.field.value
                    ? `Reserved through ${formatLongDate(reservationEndDate)}.`
                    : undefined
                }
                error={bookingDate.fieldState.error?.message}
                className="min-w-0 flex-1"
              >
                <DatePicker
                  className="text-sm"
                  aria-invalid={Boolean(bookingDate.fieldState.error) || undefined}
                  value={bookingDate.field.value}
                  min={today}
                  onChange={(next) => {
                    bookingDate.field.onChange(next);
                    desk.field.onChange('');
                  }}
                  onBlur={bookingDate.field.onBlur}
                />
              </ToolbarField>
              </>
            )}
          </div>

          {/* The form focuses the first field with an error, and the desk is
              registered first. It has no element of its own to focus, so it is
              handed one whose `focus` scrolls the map into view, message first:
              on a phone the map is far above the button, and from `lg` its top
              can be scrolled past. `scroll-mt-header` keeps it clear of the
              sticky header. */}
          <div
            ref={(node) => {
              mapRef.current = node;
              desk.field.ref(
                node && {
                  focus: () =>
                    mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
                },
              );
            }}
            className="scroll-mt-header"
          >
            {/* Above the map rather than under it: the map is tall, and a line
                at its foot was off screen or far from the eye. The availability
                state too. What the map waits on is the card's description. */}
            {!availabilityReady ? null : availability.isPending ? (
              <p className="mb-2 text-sm text-ink-muted">Checking desk availability…</p>
            ) : availability.isError ? (
              <p className="mb-2 text-sm text-danger-text">
                We couldn't load desk availability. Please try the date again.
              </p>
            ) : null}

            {desk.fieldState.error && (
              <p className="mb-2 text-sm text-danger-text">{desk.fieldState.error.message}</p>
            )}

            {/* The map's own header, picker and Book button are off because the
                page carries the booking itself. */}
            {/* Sized to fit a 13" laptop once scrolled to. The height goes on the
                map itself, since its `--map-*` defaults sit on `.seatmap` and a
                value inherited from a wrapper would lose. */}
            {/* Until the date and time are set the map is dimmed and out of
                reach (`inert`): it does not look ready, and a press does
                nothing. The card's description says what it waits on. */}
            <div className="relative">
            {/* A small note in the middle of the dimmed map while the date or
                time is missing, so its waiting has a reason in view. Outside
                the dimmed layer, so the note itself stays at full strength. */}
            {!availabilityReady && (
              <p className="pointer-events-none absolute inset-x-0 top-1/2 z-10 mx-auto w-fit max-w-[80%] -translate-y-1/2 rounded-card bg-surface px-3 py-2 text-center text-xs tracking-normal text-ink shadow-card">
                {trial
                  ? 'Choose a date and time to pick a desk.'
                  : 'Choose a package and date to pick a desk.'}
              </p>
            )}
            <div
              inert={!availabilityReady}
              className={cn(
                'transition-opacity duration-(--duration-base) ease-standard motion-reduce:transition-none',
                !availabilityReady && 'opacity-40',
              )}
            >
              <SeatMap
                className="w-full"
                style={{ '--map-max-height': 'min(42rem, calc(100svh - 10rem))' } as CSSProperties}
                space={floor}
                onSpaceChange={changeFloor}
                selected={desk.field.value || null}
                booked={unavailableDesks}
                // The map hands over `null` when a desk is unpicked; the form's empty is `''`.
                // Taken while availability still loads, so the first press is
                // never lost: the effect above empties it if the desk is taken.
                onSelect={(id) => desk.field.onChange(id ?? '')}
                showHeader={false}
                showPicker={false}
                showFooter={false}
              />
            </div>
            </div>
          </div>
        </FormSection>

        {/* Beside the map from `lg`, after it on a phone: the whole booking on
            one card. The policy and the button close it, so the button is never
            reached before everything it sends, on either layout. The map stays
            outside the `<form>`: its own buttons are not submit buttons. As tall
            as its questions, held to the top of the row. */}
        <div className="lg:self-start">
          <Form form={form} onSubmit={submit} className="flex flex-col">
            <FormSection title="Your booking" className="gap-4">
              {/* Every label above its box, so each box gets the column's full width. */}
              <div className="flex flex-col gap-3">
                <Field name="fullName" label="Full name" required>
                  <Input autoComplete="name" maxLength={160} />
                </Field>
                <Field name="email" label="Email" required>
                  <Input type="email" autoComplete="email" />
                </Field>
                <Field name="phone" label="Phone / WhatsApp" required>
                  <PhoneInput autoComplete="tel" />
                </Field>
              </div>

              {trial ? (
                // In place of the package list, between the name and the
                // policy: who the visitor is. Labels
                // above their boxes, so the boxes get the column's full width.
                <div className="flex flex-col gap-3">
                  <Field name="region" label="Lives in" required>
                    <Select
                      variant="field"
                      aria-label="Lives in"
                      options={REGION_OPTIONS}
                      placeholder="Choose your area"
                    />
                  </Field>
                  <Field name="occupation" label="Occupation" required>
                    <ChoiceList plain options={OCCUPATION_OPTIONS} className="flex-row gap-x-4" />
                  </Field>
                  {/* The follow-up questions swap with the answer above. */}
                  {/* `-mt-3`: the column's gap comes from each open part's own
                      top padding instead, so a closed one leaves no space. */}
                  <div className="-mt-3">
                    <Expand open={occupation === 'student'}>
                      <Field name="major" label="Major" required>
                        <Input maxLength={120} />
                      </Field>
                    </Expand>
                    <Expand open={occupation === 'employee'}>
                      <Field name="workMode" label="Workplace type" required>
                        <ChoiceList
                          plain
                          options={WORK_MODE_OPTIONS}
                          className="flex-row flex-wrap gap-x-4"
                        />
                      </Field>
                      <Field name="workField" label="Field of work" required>
                        <Input maxLength={120} />
                      </Field>
                    </Expand>
                  </div>
                </div>
              ) : (
                // The package is picked above the map, so all a paid booking
                // adds here is how it is paid: two short options on one line.
                <Field name="paymentMethod" label="Payment method" required>
                  <ChoiceList plain options={PAYMENT_OPTIONS} className="flex-row gap-x-4" />
                </Field>
              )}

              <PolicyConsent
                policy={BOOKING_POLICY}
                checked={policy.field.value}
                onCheckedChange={policy.field.onChange}
                error={policy.fieldState.error?.message}
              />

              {newTotal !== null && (
                <Alert tone="warning" title="The price changed" onDismiss={() => setNewTotal(null)}>
                  It is now {formatMoney(newTotal)}. Press Book on WhatsApp to send it at the new
                  price.
                </Alert>
              )}

              {/* Cleared on the next send, or by the visitor. */}
              {formError && (
                <ErrorBlock
                  title="We couldn't send this booking request"
                  error={formError.error}
                  message={formError.message}
                  onDismiss={() => setFormError(null)}
                />
              )}

              {/* Never disabled: a disabled button cannot say what is missing, a
                  click on this one can. */}
              <Button
                type="submit"
                className="self-start"
                disabled={bookingRequest.isPending}
              >
                <FaWhatsapp aria-hidden="true" />
                {bookingRequest.isPending ? 'Sending request…' : 'Book on WhatsApp'}
              </Button>
            </FormSection>
          </Form>
        </div>
      </div>
    </section>
  );
}
